import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IRecentUpdate } from '@interfaces/ISubcontractorPortal';
import { formatRelative } from '../subcontractors.format';
import styles from './ReportProgress.module.css';

type ToggleKey = 'workDone' | 'flagDelay' | 'readyForNext';

export function ReportProgress({ recentUpdates, tz }: { recentUpdates?: IRecentUpdate[]; tz: string }) {
  const { t } = useTranslation();
  const [toggles, setToggles] = useState<Record<ToggleKey, boolean>>({
    workDone: false,
    flagDelay: false,
    readyForNext: false,
  });
  const [note, setNote] = useState('');

  const handleToggle = (key: ToggleKey) =>
    setToggles((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <section className={styles.panel}>
      <header className={styles.head}>
        <h3 className={styles.title}>{t('subcontractors.report.title')}</h3>
        <p className={styles.subtitle}>{t('subcontractors.report.subtitle')}</p>
      </header>

      <div className={styles.body}>
        <div className={styles.toggleGrid}>
          {(['workDone', 'flagDelay', 'readyForNext'] as ToggleKey[]).map((key) => (
            <button
              key={key}
              type="button"
              className={styles.toggle}
              data-active={toggles[key]}
              onClick={() => handleToggle(key)}
            >
              <span className={styles.toggleDot} aria-hidden="true" />
              <span className={styles.toggleLabel}>
                {t(`subcontractors.report.${key}`)}
              </span>
            </button>
          ))}
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {t('subcontractors.report.noteLabel')}
          </span>
          <textarea
            className={styles.textarea}
            rows={3}
            placeholder={t('subcontractors.report.notePlaceholder')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>

        <div className={styles.footer}>
          <span className={styles.hint}>{t('subcontractors.report.hint')}</span>
          <button type="button" className={styles.submit}>
            {t('subcontractors.report.submit')}
          </button>
        </div>
      </div>

      {recentUpdates && recentUpdates.length > 0 ? (
        <footer className={styles.updates}>
          <span className={styles.updatesTitle}>
            {t('subcontractors.updates.title')}
          </span>
          <ul className={styles.updatesList}>
            {recentUpdates.map((u) => (
              <li key={u.id} className={styles.updateItem}>
                <span className={styles.updateDot} aria-hidden="true" />
                <span className={styles.updateBody}>{u.title}</span>
                <span className={styles.updateTime}>{formatRelative(u.timestamp, tz)}</span>
              </li>
            ))}
          </ul>
        </footer>
      ) : null}
    </section>
  );
}
